import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Search, ScanLine, UserPlus, Building, CheckCircle2 } from "lucide-react";
import { patientsData } from "@/data/mockData";
import { toast } from "sonner";

export const BookingPatientStep = ({ booking, updateBooking, initiator }) => {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("select");
  const [newPatient, setNewPatient] = useState({
    name: "", fiscalCode: "", phone: "", email: "", dateOfBirth: ""
  });

  // For citizen, the patient is themselves - skip the selection
  const isCitizen = initiator === "citizen";
  // For specialist coming from a report, the patient is locked (already known from the exam)
  const isSpecialistLocked = initiator === "specialist" && !!booking.patient;

  const filtered = useMemo(() => {
    if (!search) return patientsData.slice(0, 6);
    const s = search.toLowerCase();
    return patientsData.filter(p =>
      p.name.toLowerCase().includes(s) ||
      p.fiscalCode.toLowerCase().includes(s)
    );
  }, [search]);

  const selectPatient = (p) => {
    updateBooking({ patient: p });
    toast.success(`Paziente selezionato: ${p.name}`);
  };

  const handleNewPatient = () => {
    if (!newPatient.name || !newPatient.fiscalCode) {
      toast.error("Nome e Codice Fiscale sono obbligatori");
      return;
    }
    const created = {
      id: `new_${Date.now()}`,
      ...newPatient,
      age: 0,
      gender: "M",
      address: "",
      bloodType: "",
      allergies: [],
      chronicConditions: []
    };
    patientsData.push(created);
    updateBooking({ patient: created });
    toast.success("Nuovo paziente registrato in anagrafica");
  };

  if (isCitizen) {
    // Auto-set citizen as patient (mock)
    if (!booking.patient) {
      const me = patientsData[0];
      updateBooking({ patient: me });
    }
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold">Conferma i tuoi dati</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Stiamo prenotando a tuo nome. Verifica i dati anagrafici.
          </p>
        </div>
        {booking.patient && (
          <Card className="p-5 border-2 border-primary/30 bg-primary/5">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-primary mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold text-lg">{booking.patient.name}</p>
                <p className="text-sm text-muted-foreground font-mono">{booking.patient.fiscalCode}</p>
                <p className="text-sm text-muted-foreground">{booking.patient.email} · {booking.patient.phone}</p>
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  }

  if (isSpecialistLocked) {
    // Specialist booking a follow-up: patient is locked from the exam being reported
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold">Paziente in approfondimento</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Stai prenotando un approfondimento per il paziente del referto in lavorazione. La selezione paziente è bloccata in conformità al flusso clinico di refertazione.
          </p>
        </div>
        <Card className="p-5 border-2 border-fuchsia-300 bg-fuchsia-50/60 dark:bg-fuchsia-950/20">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="h-6 w-6 text-fuchsia-600 mt-0.5 shrink-0" />
            <div className="space-y-1 flex-1">
              <p className="font-semibold text-lg">{booking.patient.name}</p>
              <p className="text-sm text-muted-foreground font-mono">{booking.patient.fiscalCode}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-sm text-muted-foreground">
                <span>Età: <span className="text-foreground">{booking.patient.age} anni</span></span>
                <span>Sesso: <span className="text-foreground">{booking.patient.gender === "M" ? "Maschio" : "Femmina"}</span></span>
                {booking.patient.email && <span className="truncate">Email: <span className="text-foreground">{booking.patient.email}</span></span>}
                {booking.patient.phone && <span>Tel: <span className="text-foreground">{booking.patient.phone}</span></span>}
              </div>
              {booking.patient.allergies?.length > 0 && (
                <p className="text-xs text-rose-700 mt-2">
                  ⚠ Allergie: {booking.patient.allergies.join(", ")}
                </p>
              )}
              {booking.patient.chronicConditions?.length > 0 && (
                <p className="text-xs text-amber-700">
                  Patologie croniche: {booking.patient.chronicConditions.join(", ")}
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Identifica il paziente</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Seleziona da anagrafica, registra un nuovo paziente o cerca tramite tessera sanitaria.
        </p>
      </div>

      {booking.patient && (
        <Card className="p-4 border-2 border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="font-semibold text-sm">{booking.patient.name}</p>
              <p className="text-xs text-muted-foreground font-mono">{booking.patient.fiscalCode}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => updateBooking({ patient: null })} data-testid="booking-clear-patient-btn">
            Cambia
          </Button>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid grid-cols-3 w-full max-w-2xl">
          <TabsTrigger value="select" data-testid="booking-tab-select">
            <Search className="h-4 w-4 mr-2" /> Anagrafica
          </TabsTrigger>
          <TabsTrigger value="scan" data-testid="booking-tab-scan">
            <ScanLine className="h-4 w-4 mr-2" /> Tessera Sanitaria
          </TabsTrigger>
          <TabsTrigger value="new" data-testid="booking-tab-new">
            <UserPlus className="h-4 w-4 mr-2" /> Nuovo
          </TabsTrigger>
        </TabsList>

        <TabsContent value="select" className="mt-5 space-y-3">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per nome o codice fiscale..."
              className="pl-10"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="booking-search-input"
            />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {filtered.map(p => (
              <Card
                key={p.id}
                onClick={() => selectPatient(p)}
                data-testid={`booking-patient-${p.id}`}
                className="p-4 cursor-pointer hover:border-primary hover:shadow-md transition-all"
              >
                <p className="font-medium">{p.name}</p>
                <p className="text-xs text-muted-foreground font-mono">{p.fiscalCode}</p>
                <p className="text-xs text-muted-foreground mt-1">{p.age} anni · {p.gender === "M" ? "M" : "F"}</p>
              </Card>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-muted-foreground col-span-2 text-center py-6">Nessun paziente trovato</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="scan" className="mt-5 space-y-4">
          <Card className="p-6 border-dashed border-2 text-center space-y-3">
            <ScanLine className="h-12 w-12 mx-auto text-muted-foreground" />
            <p className="text-sm font-medium">Scansiona il codice a barre della Tessera Sanitaria</p>
            <p className="text-xs text-muted-foreground">
              Funzionalità integrata con html5-qrcode. Usa la pagina Pazienti per scansione completa.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toast.info("Scanner disponibile nel modulo Pazienti dell'operatore")}
              data-testid="booking-mock-scan-btn"
            >
              Apri scanner (demo)
            </Button>
          </Card>
          <Card className="p-4 bg-muted/50 border-dashed">
            <div className="flex items-start gap-3">
              <Building className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground">
                <p className="font-medium text-foreground">Ricerca in Anagrafe Regionale</p>
                <p className="mt-1">Se il paziente non è in anagrafica, il sistema interroga l'anagrafe regionale (Lombardia) tramite WS sicuro. Demo mock.</p>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="new" className="mt-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
            <div className="space-y-2">
              <Label>Nome e Cognome *</Label>
              <Input
                value={newPatient.name}
                onChange={e => setNewPatient({ ...newPatient, name: e.target.value })}
                placeholder="Mario Rossi"
                data-testid="booking-new-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Codice Fiscale *</Label>
              <Input
                value={newPatient.fiscalCode}
                onChange={e => setNewPatient({ ...newPatient, fiscalCode: e.target.value.toUpperCase() })}
                placeholder="RSSMRA80A01H501Z"
                className="font-mono"
                maxLength={16}
                data-testid="booking-new-cf"
              />
            </div>
            <div className="space-y-2">
              <Label>Data di nascita</Label>
              <Input
                type="date"
                value={newPatient.dateOfBirth}
                onChange={e => setNewPatient({ ...newPatient, dateOfBirth: e.target.value })}
                data-testid="booking-new-dob"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input
                value={newPatient.phone}
                onChange={e => setNewPatient({ ...newPatient, phone: e.target.value })}
                placeholder="+39 333 1234567"
                data-testid="booking-new-phone"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newPatient.email}
                onChange={e => setNewPatient({ ...newPatient, email: e.target.value })}
                placeholder="email@esempio.it"
                data-testid="booking-new-email"
              />
            </div>
          </div>
          <Button onClick={handleNewPatient} variant="premium" data-testid="booking-create-patient-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Registra paziente
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BookingPatientStep;
